package com.essntl.uikit.components.iconbutton

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.button.EsButtonColors
import com.essntl.uikit.components.button.EsButtonSize
import com.essntl.uikit.components.button.surface
import com.essntl.uikit.components.icons.EsIcon
import com.essntl.uikit.components.icons.EsIconSize
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.esIconPainterResource
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esTypography

@Composable
fun EsIconButton(
    onClick: () -> Unit,
    size: EsButtonSize,
    colors: EsButtonColors,
    iconRes: String,
    border: BorderStroke? = null,
    enabled: Boolean = true,
    loading: Boolean = false,
    progress: Float = 0f,
    round: Boolean = false,
    cancelable: Boolean = false,
    onCancel: () -> Unit = {},
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() },
    modifier: Modifier = Modifier,
) {
    val height = remember(size) {
        when (size) {
            EsButtonSize.Large -> EsConstants.LargeButtonHeight
            EsButtonSize.Medium -> EsConstants.MediumButtonHeight
            EsButtonSize.Small -> EsConstants.SmallButtonHeight
        }
    }
    val iconSize = remember(size) {
        when (size) {
            EsButtonSize.Large -> EsIconSize.Large
            EsButtonSize.Medium -> EsIconSize.Medium
            EsButtonSize.Small -> EsIconSize.Small
        }
    }
    val spinnerSize = remember(size) {
        when (size) {
            EsButtonSize.Large -> EsSpinnerSize.Large
            EsButtonSize.Medium -> EsSpinnerSize.Medium
            EsButtonSize.Small -> EsSpinnerSize.Small
        }
    }

    val borderRadius = remember(size) {
        if (round)
            height
        else
            when (size) {
                EsButtonSize.Large -> 16.dp
                EsButtonSize.Medium -> 12.dp
                EsButtonSize.Small -> 8.dp
            }
    }

    val isFocused by interactionSource.collectIsFocusedAsState()
    val isHovered by interactionSource.collectIsHoveredAsState()
    val isPressed by interactionSource.collectIsPressedAsState()

    val containerColor = remember(
        enabled,
        isFocused,
        isHovered,
        isPressed,
    ) {
        when {
            !enabled -> colors.disabledContainerColor
            isPressed -> colors.pressedContainerColor
            isHovered -> colors.hoveredContainerColor
            isFocused -> colors.focusedContainerColor
            else -> colors.containerColor
        }
    }
    val iconColor = remember(enabled) {
        if (enabled) colors.iconColor else colors.disabledIconColor
    }

    Box(
        contentAlignment = Alignment.Center,
        modifier = modifier
            .size(height)
            .then(
                if (isFocused && enabled && !isPressed)
                    Modifier
                        .drawBehind {
                            // TODO: Fix this hacky way of drawing the border
                            drawRoundRect(
                                color = Color.White,
                                cornerRadius = CornerRadius(borderRadius.toPx(), borderRadius.toPx()),
                                topLeft = Offset.Zero,
                                size = this.size,
                                style = Stroke(width = EsConstants.ButtonBorderWidth.toPx())
                            )
                        }
                        .drawBehind {
                            drawRoundRect(
                                color = colors.borderFocusColor,
                                cornerRadius = CornerRadius(borderRadius.toPx(), borderRadius.toPx()),
                                topLeft = Offset.Zero,
                                size = this.size,
                                style = Stroke(width = EsConstants.ButtonBorderWidth.toPx())
                            )
                        }
                else
                    Modifier
            )
            .surface(
                shape = RoundedCornerShape(borderRadius),
                backgroundColor = containerColor,
                border = if ((isFocused && enabled && !isPressed) || !enabled || (loading && !cancelable)) null else border,
                shadowElevation = 0.dp,
            )
            .pointerHoverIcon(
                icon = if (enabled && (!loading || cancelable)) PointerIcon.Hand else PointerIcon.Default,
            )
            .clickable(
                interactionSource = interactionSource,
                indication = null,
                enabled = enabled && (!loading || cancelable),
                onClick = {
                    if (loading) {
                        onCancel()
                    } else {
                        onClick()
                    }
                },
                role = Role.Button,
            ),
    ) {
        if (cancelable && loading && isHovered)
            EsIcon(
                res = EsIcons.Close,
                size = iconSize,
                tint = iconColor,
            )
        else if (progress > 0)
            CircularProgressIndicator(
                progress = { progress },
                strokeWidth = EsConstants.ButtonBorderWidth,
                color = colors.iconColor,
                trackColor = colors.borderFocusColor,
                modifier = Modifier
                    .size(iconSize.size)
            )
        else if (loading)
            EsSpinner(
                color = iconColor,
                size = spinnerSize,
                modifier = Modifier
                    .align(Alignment.Center)
            )
        else
            EsIcon(
                res = iconRes,
                size = iconSize,
                tint = iconColor,
            )
    }
}